// Una conversación con el Brain: los mensajes, el envío y la confirmación.
//
// **Sale de `BrainPage` sin cambiar nada** (2.7). Vivía dentro de la pantalla
// completa, y el panel necesita exactamente lo mismo: si se copiara, habría dos
// sitios donde arreglar la confirmación, dos donde tratar un fallo del Brain y
// dos que se irían separando. Lo que **no** sube aquí es lo que solo tiene la
// pantalla: la lista de conversaciones guardadas, el selector de agente y el
// lateral que se pliega.
//
// El historial sigue viviendo en el servidor, identificado por su hilo. Aquí
// solo está lo que hace falta para pintar y enviar.

import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { MAX_MEDIA_MB, documentMimeOf, documentTypeOf, isMediaTooBig, isTooBig, mediaTypeOf } from '@/lib/document-type'
import { capped, withAttachment } from '@/lib/attachment-message'
import { sameFileAlreadyUp } from '@/lib/same-file'
import { uploadFailure } from '@/lib/upload-failure'
import { waitForProposals, waitUntilProcessed } from '@/lib/file-proposals'
import { log } from '@/lib/logger'
import { useUploadDocument } from '@/queries/documents/mutations'
import { queryKeys } from '@/queries/keys'
import { ragApi } from '@/services/ApiService'
import { workspacesService } from '@/services/workspacesService'
import type { Pendiente } from '@/services/workspacesService'
import type { ApiDocument } from '@/lib/types'
import type { FileChoice, MensajeDelBrain } from '@/components/brain/Mensaje'
import { rememberedThread, rememberThread } from '@/lib/remembered-thread'
import {
  abrirConversacion,
  AdjuntoNoEntendido,
  BrainNoDisponible,
  cancelRun,
  confirmacionPendiente,
  contestarEncargo,
  ConversacionOcupada,
  describirAdjunto,
  enQueSeBasoDe,
  enviarMensaje,
  eventsOfRunningStep,
  leerConversacion,
  nombresDeHerramientas,
  pendingQuestions,
  RUN_IS_OVER,
} from '@/services/brainApi'
import type { Adjunto, ChosenModel, MensajeGuardado } from '@/services/brainApi'
import { useRun } from '@/hooks/use-run'
import { useDictation } from '@/hooks/use-dictation'
import { toast } from '@/lib/toast'

/** Un mensaje guardado, tal como se pinta. `waiting` dice si esta es la
 *  pregunta a la que el encargo espera respuesta: solo entonces la tarjeta de
 *  confirmar se puede pulsar. */
// How a run that ended without an answer is named. Written out and not built
// from the status, so each text is findable and a status with no text shows as
// it is instead of as a missing key.
const RUN_ENDED_LABEL: Record<string, string> = {
  done: 'brain.run_status_done',
  failed: 'brain.run_status_failed',
  cancelled: 'brain.run_status_cancelled',
  stopped_by_budget: 'brain.run_status_stopped_by_budget',
}

function runStatusLabel(status: string, t: (key: string) => string): string {
  const key = RUN_ENDED_LABEL[status]
  return key ? t(key) : status
}

// What went wrong with an attachment, said the way the person can act on it.
const WHY_NOT_UNDERSTOOD: Record<string, string> = {
  UNSUPPORTED_MEDIA_CAPABILITY: 'brain.archivo.no_soportado',
  MISSING_PROVIDER_CREDENTIAL: 'brain.archivo.sin_clave',
  DOCUMENT_UNREADABLE: 'brain.archivo.sin_texto',
}

function notUnderstood(t: (key: string, values: Record<string, string>) => string, error: unknown, nombre: string) {
  const code = error instanceof AdjuntoNoEntendido ? error.code : ''
  const detalle = error instanceof Error ? error.message : ''
  return t(WHY_NOT_UNDERSTOOD[code] ?? 'brain.archivo.no_entendido', { nombre, detalle })
}

function desdeElServidor(m: MensajeGuardado, waiting = false): MensajeDelBrain {
  const herramientas = m.trace?.herramientas
  return {
    id: m.id,
    role: m.role,
    content: m.content,
    timestamp: new Date(m.created_at),
    // El rastro y el coste vuelven con el mensaje: sin ellos una
    // conversacion recuperada pierde de donde salio cada dato.
    herramientas,
    steps: m.steps,
    coste: m.trace?.coste,
    enQueSeBaso: enQueSeBasoDe(m.trace?.estado),
    confirmacion: waiting && herramientas ? (confirmacionPendiente(herramientas) ?? undefined) : undefined,
    // La pregunta se queda en el historial aunque ya se contestara (5.4): lo
    // que se pregunto y lo que se dijo forman parte de la conversacion. Solo
    // la ultima, si el encargo sigue esperando, se puede contestar.
    questions: herramientas ? pendingQuestions(herramientas) : undefined,
    questionOpen: waiting,
  }
}

export function useBrainConversation({
  agenteId,
  onRespuesta,
  activo = true,
  donde,
}: {
  agenteId?: string
  /** Para quien lleve una lista de conversaciones y quiera refrescarla. */
  onRespuesta?: () => void
  /**
   * Si ya se ha usado. Quien vive por encima de la ruta —`LocaleLayout`— llama
   * a este hook siempre, para que la conversación **sobreviva a cerrar el panel
   * y a cambiar de página** (D7, 2.8). Pero entonces lo de arrancar se haría en
   * cada carga de la aplicación, también para quien no abra el Brain nunca, así
   * que espera a que haga falta.
   */
  activo?: boolean
  /** En qué pantalla está el usuario (3.1, D2). */
  donde?: { pagina: string; sobre?: { tipo: string; id: string }; idioma?: string }
} = {}) {
  const { t } = useTranslation()
  // **La subida la hace el panel, no el Brain** (D4, 5.1): una llamada suya es
  // texto y el ejecutor solo manda JSON, asi que un archivo no cabe. Se
  // reutiliza la mutacion que ya existe -no la de `use-documents`, que arrastra
  // la consulta entera de documentos y este hook vive en toda la aplicacion-.
  const subida = useUploadDocument()
  const consultas = useQueryClient()
  const [messages, setMessages] = useState<MensajeDelBrain[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  // Como se llama cada herramienta en palabras. El rastro solo trae el nombre
  // tecnico, y `get_api_agent_v1_agents` no es algo que enseñarle a nadie.
  const [nombresHerramientas, setNombresHerramientas] = useState<Map<string, string>>(new Map())
  // Un hilo por sesion: sin el, cada peticion empieza de cero y «vuelve a
  // activarlo» no sabe de que se hablaba.
  const [hiloActivo, setHiloActivo] = useState<string>('')
  // Cual propuesta se esta guardando, para no dejar pulsar dos veces.
  const [resolvingProposal, setResolvingProposal] = useState('')
  // **El archivo que se esta leyendo ahora mismo** (3.4, D6, CA8). Entre
  // «estoy leyendo» y la propuesta pasa hasta un minuto, y hasta el
  // 2026-09-23 en ese minuto no se movia nada: en una conexion lenta eso se
  // lee como que se colgo, y lo que hace cualquiera entonces es volver a
  // pulsar. Guarda el nombre, no un booleano, porque lo que hay que ensenar
  // es que se esta leyendo **eso**.
  const [readingFile, setReadingFile] = useState('')
  // **En que conversacion estamos, para lo que tarda** (5.6). La espera de la
  // propuesta dura hasta un minuto, y si en ese rato se empieza de nuevo, lo
  // que llegue cae dentro de la conversacion nueva hablando de un archivo del
  // que ya no hay rastro. Es una referencia y no el estado porque quien tiene
  // que comprobarlo es una funcion que arranco antes del cambio: el estado
  // que ella capturo es el viejo, y esto es lo de ahora.
  const currentThread = useRef('')
  // **El encargo que contesta ahora mismo en esta conversacion.** Todo mensaje
  // se trabaja en segundo plano: se manda, se guarda, y la respuesta llega
  // cuando termina. Mientras no es `null` el Brain esta pensando o waiting
  // a que se le conteste.
  const [encargoActivo, setEncargoActivo] = useState<string | null>(null)
  // Sube cada vez que se le contesta: vuelve a abrir el seguimiento (ver `useRun`).
  const [vuelta, setVuelta] = useState(0)
  // Mientras llega la conversacion recordada al arrancar. No se deja enviar en
  // ese rato: el mensaje nuevo podria llegar tambien dentro de lo recuperado y
  // pintarse dos veces. Dura lo que tarda una lectura.
  const [restaurando, setRestaurando] = useState(false)
  const encargo = useRun(encargoActivo, vuelta)
  // **El modelo de esta conversación, si se eligió uno** (D9, 7.2). `null` es
  // el del agente. Va con cada mensaje y con cada respuesta.
  const [modelo, setModelo] = useState<ChosenModel | null>(null)
  // Si lo que se esta waiting es una confirmacion, al terminar hay que
  // refrescar lo que haya en pantalla: el cambio lo hizo el Brain en el
  // servidor y la pantalla de detras no tiene forma de enterarse sola.
  const refrescarAlTerminar = useRef(false)

  // Una sola vez, y no antes de que haga falta.
  const yaPedidos = useRef(false)
  useEffect(() => {
    if (!activo || yaPedidos.current) return
    yaPedidos.current = true
    // Con `.catch` porque el Brain caido no puede tumbar el panel antes de
    // abrirlo (6.1, R6). Sin nombres el rastro se lee con el nombre tecnico,
    // que es peor y no es un fallo.
    nombresDeHerramientas()
      .then(setNombresHerramientas)
      .catch((error) => {
        log.error('No se pudieron leer los nombres de las herramientas', error, {
          component: 'useBrainConversation',
        })
      })
  }, [activo])

  useEffect(() => {
    currentThread.current = hiloActivo
    if (hiloActivo) rememberThread(hiloActivo)
  }, [hiloActivo])


  // Dictating lands in the box, not sent (brain-multimodal, D7): the person
  // reads it first. A failure says which: no microphone, or not understood.
  const alDictar = useCallback(
    (texto: string) => setInputValue((previo) => (previo.trim() ? `${previo.trim()} ${texto}` : texto)),
    [],
  )
  const siFallaElDictado = useCallback(
    (motivo: 'microphone' | 'transcription') =>
      toast.error(t(motivo === 'microphone' ? 'brain.dictado.sin_microfono' : 'brain.dictado.no_entendido')),
    [t],
  )
  const dictado = useDictation({ onText: alDictar, onError: siFallaElDictado, agenteId })
  const costeDelDictado = dictado.takeCost

  const handleSendMessage = useCallback(
    async (message?: string, adjunto?: Adjunto) => {
      const messageToSend = message || inputValue.trim()
      if (!messageToSend || isLoading || restaurando) return
      // What dictating it cost goes with it, as an attachment's does (R12).
      adjunto = adjunto ?? costeDelDictado() ?? undefined

      const userMessage: MensajeDelBrain = {
        id: Date.now().toString(),
        content: messageToSend,
        role: 'user',
        timestamp: new Date(),
      }

      setMessages((prev) => [...prev, userMessage])
      // Solo se vacía la caja si lo que se manda salió de ella: contestar con
      // una opción o avisar de una subida no se lleva lo que se estaba escribiendo.
      if (!message) setInputValue('')
      setIsLoading(true)

      const loadingMessage: MensajeDelBrain = {
        id: `loading-${Date.now()}`,
        content: '',
        role: 'assistant',
        timestamp: new Date(),
        isGenerating: true,
      }
      setMessages((prev) => [...prev, loadingMessage])

      try {
        if (encargoActivo && encargo?.status === 'waiting_for_person') {
          // **Si el Brain esta waiting, lo que se escribe es la respuesta**:
          // «no, mejor el otro», «si pero antes...». Sin accion aprobada, asi
          // que no confirma nada por su cuenta; el Brain sigue desde ahi.
          // La tarjeta de confirmar deja de estar en juego: se contesto hablando.
          // Y la pregunta con opciones igual: contestada con la tarjeta o
          // escribiendo, ya no espera nada. Se queda escrita, cerrada.
          setMessages((prev) =>
            prev.map((m) =>
              m.confirmacion || m.questionOpen ? { ...m, confirmacion: undefined, questionOpen: false } : m,
            ),
          )
          await contestarEncargo(encargoActivo, messageToSend, [], donde, modelo, adjunto)
          setVuelta((v) => v + 1)
          return
        }
        // Se manda y se vuelve enseguida: la pregunta ya esta guardada y el
        // encargo la trabaja aunque se recargue la pagina. La respuesta la trae
        // el efecto que sigue al encargo.
        setEncargoActivo(await enviarMensaje(messageToSend, hiloActivo, { agenteId, donde, modelo, adjunto }))
        // La conversacion ya existe con su titulo: la lista puede pintarla.
        onRespuesta?.()
      } catch (error) {
        if (error instanceof ConversacionOcupada) {
          // No se guardo: se dice, y se sigue mirando el que si esta en marcha.
          setMessages((prev) =>
            prev
              .filter((m) => m.id !== loadingMessage.id && m.id !== userMessage.id)
              .concat({
                id: `${Date.now()}`,
                role: 'assistant',
                timestamp: new Date(),
                content: t('brain.ocupado'),
              }),
          )
          if (error.runId) setEncargoActivo(error.runId)
        } else {
          log.error('Fallo al enviar', error, { component: 'useBrainConversation' })
          setMessages((prev) =>
            prev.filter((m) => m.id !== loadingMessage.id).concat({
              id: `${Date.now()}`,
              role: 'assistant',
              timestamp: new Date(),
              content:
                error instanceof BrainNoDisponible
                  ? error.message
                  : 'Lo siento, no pude procesar tu consulta. Por favor, inténtalo de nuevo.',
            }),
          )
        }
      } finally {
        setIsLoading(false)
      }
    },
    [inputValue, isLoading, restaurando, hiloActivo, agenteId, donde, modelo, onRespuesta, t, encargoActivo, encargo?.status, costeDelDictado],
  )

  // **Cuando el encargo termina o se para a preguntar**, lo que dijo ya esta
  // guardado: se trae del servidor y ocupa el sitio del «pensando». No se
  // repinta la conversacion entera, a proposito: borraria lo que solo vive en
  // la pantalla -los avisos de una subida, las propuestas de un archivo-.
  // **Mientras piensa, lo que va haciendo** (3.4): cada aviso del encargo trae
  // sus eventos y se pintan bajo el «pensando». Lo pinta el mensaje, que es el
  // mismo en el panel y en la pantalla completa.
  useEffect(() => {
    if (!encargo?.events) return
    const liveSteps = eventsOfRunningStep(encargo)
    const plan = encargo.plan?.length ? encargo.plan : undefined
    setMessages((prev) => prev.map((m) => (m.isGenerating ? { ...m, liveSteps, plan } : m)))
  }, [encargo])

  const runStatus = encargo?.status
  useEffect(() => {
    if (!encargoActivo || !runStatus) return
    const waiting = runStatus === 'waiting_for_person'
    if (!waiting && !RUN_IS_OVER.has(runStatus)) return

    const hilo = currentThread.current
    let vivo = true
    void leerConversacion(hilo).then((guardados) => {
      if (!vivo || currentThread.current !== hilo) return
      const ultimo = guardados[guardados.length - 1]
      const respuesta: MensajeDelBrain =
        ultimo?.role === 'assistant'
          ? desdeElServidor(ultimo, waiting)
          : {
              // Termino sin respuesta: fallo, se cancelo o llego a su tope. Se
              // dice, en vez de dejar el «pensando» girando para siempre.
              id: `${Date.now()}`,
              role: 'assistant',
              timestamp: new Date(),
              content: t('brain.sin_respuesta', { estado: runStatusLabel(runStatus, t) }),
            }
      setMessages((prev) =>
        prev.filter((m) => !m.isGenerating && m.id !== respuesta.id).concat(respuesta),
      )
      if (!waiting) {
        setEncargoActivo(null)
        onRespuesta?.()
        if (refrescarAlTerminar.current) {
          refrescarAlTerminar.current = false
          void consultas.invalidateQueries()
        }
      }
    })
    return () => {
      vivo = false
    }
  }, [encargoActivo, runStatus, onRespuesta, consultas, t])

  // Confirmar es **reenviar la peticion con la accion aprobada**, no escribir
  // «confirmo»: la guarda vive en el servidor y no lee prosa. Se comprobo el
  // 2026-08-24 respondiendole «Confirmo» y viendo que el agente seguia ahi.
  //
  // Dentro de un encargo, la respuesta es **un mensaje de la persona** que
  // lleva la accion aprobada, y el encargo sigue desde el: asi queda en el
  // historial «te lo pregunte y dijiste que si».
  const confirmar = useCallback(
    async (mensaje: MensajeDelBrain) => {
      if (!mensaje.confirmacion || !encargoActivo) return
      const si = t('brain.confirmar_si_mensaje')
      setMessages((previos) => [
        ...previos.map((m) =>
          m.id === mensaje.id ? { ...m, confirmacionResuelta: 'confirmada' as const } : m,
        ),
        { id: `${Date.now()}`, role: 'user', timestamp: new Date(), content: si },
        { id: `loading-${Date.now()}`, role: 'assistant', timestamp: new Date(), content: '', isGenerating: true },
      ])
      refrescarAlTerminar.current = true
      try {
        await contestarEncargo(encargoActivo, si, [mensaje.confirmacion.nombre], donde, modelo)
        setVuelta((v) => v + 1)
      } catch (error) {
        log.error('No se pudo contestar al encargo', error, { component: 'useBrainConversation' })
        refrescarAlTerminar.current = false
      }
      // **Y la pantalla de detras se entera** (4.7, D7, CA9). Esto es mas
      // dificil que aceptar una propuesta: ahi decide la pantalla, y aqui el
      // cambio lo hace el Brain **desde el servidor**, asi que ella no tiene
      // forma de enterarse sola de algo que no ha hecho.
      //
      // Se invalida todo lo que hay en pantalla y no solo los documentos, a
      // proposito: lo que se acaba de confirmar puede ser cualquier cosa -un
      // agente, una herramienta, un documento-, y mirar el nombre de la
      // operacion para adivinar que consulta tocar ataria el panel a como se
      // llaman las herramientas, que salen del contrato y cambian con el. Solo
      // se vuelve a pedir lo que este montado.
    },
    [encargoActivo, donde, modelo, t],
  )

  const cancelar = useCallback((mensaje: MensajeDelBrain) => {
    // El encargo se para de verdad: si no, seguiria waiting una respuesta
    // que no va a llegar y la conversacion no aceptaria el siguiente mensaje.
    if (encargoActivo) {
      void cancelRun(encargoActivo).catch((error) =>
        log.error('No se pudo cancelar el encargo', error, { component: 'useBrainConversation' }),
      )
      setEncargoActivo(null)
    }
    setMessages((previos) => [
      ...previos.map((m) =>
        m.id === mensaje.id ? { ...m, confirmacionResuelta: 'cancelada' as const } : m,
      ),
      {
        id: `${Date.now()}`,
        role: 'assistant',
        timestamp: new Date(),
        // Se dice que no se hizo nada. Cancelar en silencio deja la duda de si
        // se ejecuto igual, que es justo lo que la confirmacion viene a quitar.
        content: `No he hecho nada: has cancelado «${mensaje.confirmacion?.nombre_visible}».`,
      },
    ])
  }, [encargoActivo])

  /** Traer una conversación guardada por su hilo. */
  /** Parar lo que el Brain esta haciendo. Marca el encargo como cancelado; el
   *  paso en curso termina y el siguiente no empieza, y el efecto que sigue al
   *  encargo pone en su sitio que no se termino. */
  const detener = useCallback(() => {
    if (!encargoActivo) return
    void cancelRun(encargoActivo).catch((error) =>
      log.error('No se pudo detener el encargo', error, { component: 'useBrainConversation' }),
    )
  }, [encargoActivo])

  /** Traer una conversacion guardada, **y retomar lo que siga en marcha**: si
   *  el Brain aun trabaja en ella, se vuelve a ver pensando. Si espera
   *  respuesta, la tarjeta de confirmar la activa el mismo efecto que sigue al
   *  encargo, al ver su estado.
   *
   *  `conservar` es para el arranque: lo guardado llega un momento despues, y
   *  en ese rato ya se puede haber hecho algo -soltar un archivo, escribir-.
   *  Entonces lo guardado va delante y lo nuevo se queda, en vez de pisarlo. */
  const cargarHilo = useCallback(async (hilo: string, { conservar = false } = {}) => {
    setHiloActivo(hilo)
    currentThread.current = hilo
    if (!conservar) setEncargoActivo(null)
    const { mensajes, encargoActivo: enMarcha, modelo: suModelo } = await abrirConversacion(hilo)
    if (currentThread.current !== hilo) return
    // **Cada conversación con su modelo** (7.3): el que se eligió en ella, o
    // el del agente. Al arrancar, lo que se hubiera elegido ya se respeta.
    setModelo((antes) => (conservar && antes ? antes : suModelo))
    const pintados = mensajes.map((m) => desdeElServidor(m))
    if (enMarcha && enMarcha.status !== 'waiting_for_person') {
      pintados.push({
        id: `loading-${Date.now()}`,
        role: 'assistant',
        timestamp: new Date(),
        content: '',
        isGenerating: true,
      })
    }
    setMessages((antes) => (conservar ? [...pintados, ...antes] : pintados))
    // Si mientras tanto ya se mando algo, manda lo de ahora.
    setEncargoActivo((antes) => (conservar && antes ? antes : (enMarcha?.id ?? null)))
  }, [])

  /** Abrir una conversacion de la lista: sustituye a la que hubiera. */
  const abrirHilo = useCallback((hilo: string) => cargarHilo(hilo), [cargarHilo])

  // **Recargar no saca de la conversacion.** Al arrancar se abre la ultima en
  // la que se estaba, y con ella lo que el Brain siga haciendo; sin recuerdo,
  // se empieza una nueva.
  useEffect(() => {
    if (!activo || hiloActivo) return
    const recordado = rememberedThread()
    if (recordado) {
      setRestaurando(true)
      void cargarHilo(recordado, { conservar: true }).finally(() => setRestaurando(false))
    } else setHiloActivo(`brain-${Date.now()}`)
  }, [activo, hiloActivo, cargarHilo])

  /**
   * Traer a la conversacion lo que el agente propone recordar del archivo
   * (5.2, CA3). Se acepta aqui mismo: el formulario de Knowledge deja de ser
   * la unica puerta, no deja de existir.
   */
  const proposeFromFile = useCallback(
    async (documentId: string, nombre: string) => {
      const say = (content: string, proposals?: Pendiente[]): MensajeDelBrain => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        role: 'assistant',
        timestamp: new Date(),
        content,
        fileProposals: proposals,
      })

      // La conversacion en la que arranco esto (5.6). Si al volver ya no es la
      // misma, lo que se traiga no es de nadie.
      const arranco = currentThread.current

      let fromThisFile: Pendiente[]
      try {
        fromThisFile = await waitForProposals({
          documentId,
          ask: () => workspacesService.pendientes(),
        })
      } catch (error) {
        // Que no se pueda preguntar no tumba la conversacion: el archivo esta
        // subido y la propuesta sigue waiting donde siempre. **Pero no se
        // calla** (5.3): hasta el 2026-09-23 esto volvia en silencio, y un
        // silencio despues de «estoy leyendo» se lee como que sigue leyendo.
        // Se dice lo que pasa y se pide que lo escriba una persona.
        log.error('No se pudo leer lo que propone el archivo', error, {
          component: 'useBrainConversation',
        })
        if (currentThread.current === arranco) {
          setMessages((previos) => [
            ...previos,
            say(t('brain.archivo.no_pude_leer', { nombre }), []),
          ])
        }
        return
      } finally {
        if (currentThread.current === arranco) setReadingFile('')
      }

      if (currentThread.current !== arranco) return

      setMessages((previos) => [
        ...previos,
        fromThisFile.length > 0
          ? say(t('brain.archivo.he_leido', { nombre }), fromThisFile)
          : say(t('brain.archivo.sigue_leyendo', { nombre })),
      ])
    },
    [t],
  )

  /**
   * El si o el no, desde la conversacion. La red es **la misma que usa la
   * puerta de Knowledge**: lo que cambia es donde se lee, no como se resuelve.
   */
  const resolveProposal = useCallback(
    async (
      messageId: string,
      proposal: Pendiente,
      decision: 'aprobar' | 'rechazar',
      motivo: string,
    ) => {
      setResolvingProposal(proposal.id)
      try {
        await workspacesService.resolverPendiente({
          que: proposal.que,
          id: proposal.id,
          decision,
          por: t('knowledge.puerta.firma'),
          motivo,
        })
        setMessages((previos) =>
          previos.map((one) =>
            one.id === messageId
              ? {
                  ...one,
                  fileProposalsResolved: {
                    ...(one.fileProposalsResolved ?? {}),
                    [proposal.id]: decision === 'aprobar' ? 'accepted' : 'rejected',
                  },
                }
              : one,
          ),
        )
        // **La pantalla de detras se entera.** Sin esto, decidir aqui no llega
        // alli: un documento nace fuera de la busqueda y entra al aceptar la
        // propuesta, asi que Conocimiento seguia diciendo «sin usar» de algo
        // que ya se usaba. Se dice que si, se mira, y la pantalla contradice.
        // Se invalida tambien al rechazar porque lo que cambia es el estado de
        // lo que espera, y eso se ve igual desde detras.
        await consultas.invalidateQueries({ queryKey: queryKeys.documents.all })
      } catch (error) {
        log.error('No se pudo resolver la propuesta', error, {
          component: 'useBrainConversation',
        })
      } finally {
        setResolvingProposal('')
      }
    },
    [t, consultas],
  )


  /** Subir un archivo y pedirle al Brain que lo archive (5.1, R4; 6.1).
   *
   * **Un solo camino** (6.1, D8): el archivo se sube como documento y lo que se
   * le dice al Brain es un mensaje como cualquier otro -«He subido X,
   * propónme dónde archivarlo»-, que se guarda y va por el encargo. Hasta el
   * 2026-09-26 eran tres piezas: «He subido X» y «Estoy leyendo X» solo en
   * pantalla, que recargar borraba, y una petición larga e invisible por el
   * camino viejo, sin encargo. Lo que se ve ahora es lo que se guarda y lo que
   * recibe el Brain. Mientras sube, lo dice el indicador de lectura.
   */
  // The document waiting for «only here» or «keep it», by the id of the
  // message that asks. In memory on purpose: nothing is uploaded until then.
  const archivosPorDecidir = useRef(new Map<string, File>())

  const subirArchivo = useCallback(
    async (archivo: File, destino?: FileChoice) => {
      const documentType = documentTypeOf(archivo.name)
      const ahora = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
      const decir = (content: string): MensajeDelBrain => ({
        id: ahora(),
        role: 'assistant',
        timestamp: new Date(),
        content,
      })

      // Una foto, un audio o un video no se archivan: se entienden una vez y
      // su texto va en el mensaje (brain-multimodal, D3 y D4).
      const media = mediaTypeOf(archivo)
      if (!documentType && media) {
        const kindLabel = {
          image: t('brain.archivo.tipo_image'),
          audio: t('brain.archivo.tipo_audio'),
          video: t('brain.archivo.tipo_video'),
        }[media.type]
        if (isMediaTooBig(archivo, media.type)) {
          setMessages((previos) => [
            ...previos,
            decir(
              t('brain.archivo.adjunto_grande', { nombre: archivo.name, mb: MAX_MEDIA_MB[media.type], tipo: kindLabel }),
            ),
          ])
          return
        }
        setReadingFile(archivo.name)
        let entendido: Awaited<ReturnType<typeof describirAdjunto>>
        try {
          entendido = await describirAdjunto(archivo, media, agenteId)
        } catch (error) {
          log.error('No se pudo entender el adjunto', error, { component: 'useBrainConversation' })
          setReadingFile('')
          setMessages((previos) => [...previos, decir(notUnderstood(t, error, archivo.name))])
          return
        }
        setReadingFile('')
        await handleSendMessage(
          withAttachment(
            t('brain.archivo.te_paso', { nombre: archivo.name, tipo: kindLabel }),
            archivo.name,
            capped(entendido.texto, t('brain.archivo.cortado')),
          ),
          entendido.adjunto,
        )
        return
      }

      setReadingFile(archivo.name)

      if (!documentType) {
        setReadingFile('')
        setMessages((previos) => [
          ...previos,
          decir(t('brain.archivo.formato_no_admitido', { nombre: archivo.name })),
        ])
        return
      }

      // **Se pregunta el peso antes de subir** (3.1). Hasta el 2026-09-23 no
      // se preguntaba en ningun momento por aqui: el archivo viajaba entero
      // para que el servidor lo rechazara al final, o sea que la espera mas
      // larga era la del que iba a fallar seguro.
      if (isTooBig(archivo)) {
        setReadingFile('')
        setMessages((previos) => [
          ...previos,
          decir(t('brain.archivo.demasiado_grande', { nombre: archivo.name })),
        ])
        return
      }

      // **Primero se pregunta si se guarda** (brain-multimodal, D6): no todo
      // lo que se le pasa al Brain debe acabar en el Knowledge, y rechazar la
      // propuesta despues no lo borra. La pregunta es de la persona, asi que
      // la hace la pantalla: al modelo le costaria un turno entero.
      if (!destino) {
        setReadingFile('')
        const pregunta = { ...decir(t('brain.archivo.que_hago', { nombre: archivo.name })), fileChoice: 'pending' as const }
        archivosPorDecidir.current.set(pregunta.id, archivo)
        setMessages((previos) => [...previos, pregunta])
        return
      }

      if (destino === 'read') {
        let leido: Awaited<ReturnType<typeof describirAdjunto>>
        try {
          leido = await describirAdjunto(
            archivo,
            { type: 'document', mime: archivo.type || documentMimeOf(archivo.name) },
            agenteId,
          )
        } catch (error) {
          log.error('No se pudo leer el documento sin guardarlo', error, { component: 'useBrainConversation' })
          setReadingFile('')
          setMessages((previos) => [...previos, decir(notUnderstood(t, error, archivo.name))])
          return
        }
        setReadingFile('')
        await handleSendMessage(
          withAttachment(
            t('brain.archivo.te_paso_solo_aqui', { nombre: archivo.name }),
            archivo.name,
            capped(leido.texto, t('brain.archivo.cortado')),
          ),
          leido.adjunto,
        )
        return
      }

      let subidoId: string
      setIsLoading(true)
      try {
        // **El mismo archivo otra vez** (4.9, CA10, D8). Es lo que pasa cada
        // mes con la tarifa, y antes quedaban dos documentos iguales sin que
        // nadie lo notara: el agente respondia con los dos. Se pregunta antes
        // de subir, que es la unica forma de que no llegue a haber dos.
        //
        // Si la consulta falla no se para la subida: no saberlo es peor que
        // arriesgarse a un duplicado, porque lo que el usuario pidio fue subir.
        const yaEsta = await ragApi
          .getDocuments({})
          .then((documentos) => sameFileAlreadyUp(documentos as ApiDocument[], archivo.name))
          .catch((error) => {
            log.error('No se pudo comprobar si el archivo ya estaba', error, {
              component: 'useBrainConversation',
            })
            return null
          })
        if (yaEsta) {
          setReadingFile('')
          setMessages((previos) => [
            ...previos,
            decir(
              t('brain.archivo.ya_existe', { nombre: archivo.name, titulo: yaEsta.title }),
            ),
          ])
          return
        }

        const subido = await subida.mutateAsync({
          title: archivo.name,
          document_type: documentType,
          file: archivo,
        })
        subidoId = String(subido.id)
        // **Sin `await`**: esperar aqui dejaria el panel sin poder escribir
        // hasta un minuto, y lo que se esta waiting no lo ha pedido nadie.
        // La conversacion sigue viva y la propuesta entra cuando llega.
        void proposeFromFile(subidoId, archivo.name)
      } catch (error) {
        log.error('No se pudo subir el archivo desde el panel', error, {
          component: 'useBrainConversation',
        })
        // **Que pasa de verdad, no una frase para todos** (5.3, D6): pesar de
        // mas, la sesion caducada y el servicio caido se arreglan de tres
        // formas distintas, y con un solo mensaje quien lo lee no sabe cual.
        setReadingFile('')
        const { key, detail } = uploadFailure(error)
        setMessages((previos) => [
          ...previos,
          decir(t(key, { nombre: archivo.name, detalle: detail })),
        ])
        return
      } finally {
        setIsLoading(false)
      }
      // Antes de anunciarlo, a que se pueda leer (brain-multimodal, D2): el
      // Brain lo lee por su id en ese mismo turno, y sin texto todavia solo
      // podria decir que se esta procesando. El indicador de lectura sigue.
      await waitUntilProcessed({
        ask: () => ragApi.getDocument(subidoId) as Promise<{ status?: string }>,
      })
      // Y la otra propuesta, la de como llamarlo y donde ponerlo, que es la
      // que se puede contestar ya: la pide el mensaje, y el Brain la contesta
      // mirando el archivador. Fuera del `try`, cuando ya no se esta subiendo.
      await handleSendMessage(t('brain.archivo.he_subido', { nombre: archivo.name, id: subidoId }))
    },
    [subida, t, proposeFromFile, handleSendMessage, agenteId],
  )

  const nuevoHilo = useCallback(() => {
    const nuevo = `brain-${Date.now()}`
    currentThread.current = nuevo
    setHiloActivo(nuevo)
    setEncargoActivo(null)
    setMessages([])
    // Una conversación nueva empieza con el modelo del agente (7.3).
    setModelo(null)
    // La espera que hubiera corriendo deja de tener a quien contarle nada, asi
    // que tampoco tiene por que seguir diciendo que lee.
    setReadingFile('')
  }, [])

  return {
    messages,
    inputValue,
    setInputValue,
    // Pensando es ocupado; waiting a que se conteste, no: ahi se puede pulsar
    // la tarjeta o escribir la respuesta.
    isLoading:
      isLoading || restaurando || (encargoActivo !== null && encargo?.status !== 'waiting_for_person'),
    nombresHerramientas,
    hiloActivo,
    handleSendMessage,
    modelo,
    elegirModelo: setModelo,
    // Contestar a la pregunta del Brain es mandar un mensaje mientras espera
    // (5.3): el mismo camino que escribir en la caja, que ya contesta al
    // encargo. La tarjeta solo pone el texto.
    responder: (_mensaje: MensajeDelBrain, respuesta: string) => void handleSendMessage(respuesta),
    detener,
    confirmar,
    cancelar,
    abrirHilo,
    nuevoHilo,
    subirArchivo,
    dictar: dictado.toggle,
    dictando: dictado.recording,
    transcribiendo: dictado.transcribing,
    microfono: dictado.stream,
    elegirArchivo: (mensaje: MensajeDelBrain, destino: FileChoice) => {
      const archivo = archivosPorDecidir.current.get(mensaje.id)
      if (!archivo) return
      archivosPorDecidir.current.delete(mensaje.id)
      setMessages((previos) => previos.map((m) => (m.id === mensaje.id ? { ...m, fileChoice: destino } : m)))
      void subirArchivo(archivo, destino)
    },
    resolveProposal,
    resolvingProposal,
    readingFile,
  }
}
